import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { API_BASE } from "../lib/api";
import { ShieldCheck, XCircle, HelpCircle, ExternalLink } from "lucide-react";

export default function PublicVerify() {
  const { code } = useParams();
  const [state, setState] = useState({ loading: true, data: null });

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/public/verify/${encodeURIComponent(code)}`);
        const data = await res.json();
        setState({ loading: false, data });
      } catch (e) {
        setState({ loading: false, data: { status: "not_found" } });
      }
    })();
  }, [code]);

  const StatusCard = () => {
    const { data } = state;
    if (!data) return null;
    if (data.status === "valid") {
      return (
        <div data-testid="verify-valid" className="border border-emerald-200 bg-emerald-50 rounded-sm p-8 sm:p-10 text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-sm bg-emerald-600 text-white mb-4">
            <ShieldCheck size={32} />
          </div>
          <div className="text-xs uppercase tracking-widest text-emerald-700 font-semibold mb-1">Certificato Valido</div>
          <h1 className="text-3xl text-emerald-900 mb-4 wordmark">Autenticità verificata</h1>
          <p className="text-lg text-emerald-900 leading-relaxed max-w-2xl mx-auto">
            Si certifica che <strong>{data.first_name} {data.last_name}</strong> ha superato con esito positivo il corso{" "}
            <strong>«{data.course_title}»</strong> in data{" "}
            <strong>{new Date(data.issued_at).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</strong>.
          </p>
          <div className="mt-6 text-xs text-emerald-800/70 font-mono">Codice: {data.code}</div>
        </div>
      );
    }
    if (data.status === "revoked") {
      return (
        <div data-testid="verify-revoked" className="border border-red-200 bg-red-50 rounded-sm p-8 sm:p-10 text-center">
          <div className="inline-flex h-16 w-16 items-center justify-center rounded-sm bg-red-600 text-white mb-4">
            <XCircle size={32} />
          </div>
          <div className="text-xs uppercase tracking-widest text-red-700 font-semibold mb-1">Certificato Revocato</div>
          <h1 className="text-3xl text-red-900 mb-4 wordmark">Non più valido</h1>
          <p className="text-base text-red-900 max-w-2xl mx-auto">
            Il certificato con codice <strong>{data.code}</strong> è stato revocato in data{" "}
            <strong>{data.revoked_at ? new Date(data.revoked_at).toLocaleDateString("it-IT") : "sconosciuta"}</strong>.
          </p>
        </div>
      );
    }
    return (
      <div data-testid="verify-not-found" className="border border-slate-200 bg-slate-50 rounded-sm p-8 sm:p-10 text-center">
        <div className="inline-flex h-16 w-16 items-center justify-center rounded-sm bg-slate-400 text-white mb-4">
          <HelpCircle size={32} />
        </div>
        <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold mb-1">Non trovato</div>
        <h1 className="text-3xl text-slate-800 mb-4 wordmark">Certificato non trovato</h1>
        <p className="text-base text-slate-700 max-w-2xl mx-auto">
          Il codice fornito potrebbe essere errato o il certificato potrebbe non esistere.
          Ricontrolla il codice riportato sul certificato.
        </p>
      </div>
    );
  };

  return (
    <div data-testid="public-verify-page" className="min-h-screen bg-brand-bg brand-grid-bg">
      <header className="bg-white border-b border-brand-line h-16 flex items-center px-4 sm:px-6 lg:px-8">
        <Link to="/" className="flex items-center gap-2.5">
          <img
            src={`${process.env.PUBLIC_URL}/logo-academy-full.png`}
            alt="Buro Academy"
            className="h-8 w-auto"
          />
        </Link>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {state.loading ? (
          <div className="text-center text-brand-muted py-20">Verifica in corso…</div>
        ) : (
          <>
            <StatusCard />
            <div className="mt-8 text-center text-sm text-brand-muted">
              <Link to="/" className="text-brand-blue hover:underline underline-offset-4 inline-flex items-center gap-1">
                Torna a Buro Academy <ExternalLink size={12} />
              </Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
